import { Settings } from 'luxon';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { setIdOnReporters } from 'dashboard/lib/reporting';
import { Environment } from 'dashboard/models/environment';
import { Organization } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { Role } from 'dashboard/models/role';
import { User } from 'dashboard/models/user';
import { CurrentUserSessionMeta, UserSession } from 'dashboard/models/user-session';
import {
  saveCurrentUser,
  saveReadmeJwtToken,
  setImpersonationMode,
} from 'dashboard/redux/actions/current-user';
import { setUserSession } from 'dashboard/redux/actions/current-user-session';
import { saveFeatureFlags } from 'dashboard/redux/actions/feature-flags';
import {
  setCurrentOrganizationPlan,
  setEnvironments,
  setOrganization,
  setPendingOrganizationPlanMigration,
} from 'dashboard/redux/actions/organization';
import { setAndPersistCurrentEnvironment } from 'dashboard/redux/thunk-actions/current-environment';
import {
  loadNewAndLegacyInquiryTemplateFilterOptionList,
  loadNewAndLegacyInquiryTemplateList,
  loadThemeList,
} from 'dashboard/redux/thunk-actions/organization';
import { loadReportTemplatesList } from 'dashboard/redux/thunk-actions/report-templates';
import { DashboardThunkAction } from 'dashboard/redux/types';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import { ApiEndpoint } from 'lib/interfaces/api';
import { isPermissionsError } from 'lib/permissions-errors';
import { captureException } from 'lib/reporting';

interface FetchDashboardDataArgs extends CurrentUserSessionMeta {
  user: User;
  userSession: UserSession;
  roles: Role[];
}

/**
 * Should be called when dashboard mounts with user authenticated. This loads
 * the data needed for most Dashboard pages to render something meaningful.
 * - Authentication
 * - Session refresh
 */
export const fetchDashboardData =
  ({
    'access-token': accessToken,
    'refresh-token': refreshToken,
    'expires-at': expiresAt,
    'feature-flags': featureFlags,
    'impersonation-session': impersonationSession,
    'readme-jwt-token': readmeJwtToken,
    user,
    userSession,
    roles,
  }: FetchDashboardDataArgs): DashboardThunkAction =>
  async (dispatch): Promise<void> => {
    SessionClientDashboard.setTokens(refreshToken, accessToken);
    SessionClientDashboard.expiresAt = expiresAt;

    dispatch(setImpersonationMode(impersonationSession));
    dispatch(saveFeatureFlags(featureFlags));
    dispatch(saveReadmeJwtToken(readmeJwtToken));

    // This sets the default environment for all dashboard requests.
    const { data: organization, included } =
      await SessionClientDashboard.getResourceSingleton<Organization>(ApiEndpoint.Organization);

    const environments = organization.relationships['environments'].data.map((envData) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      included.find((i) => i.id === envData.id),
    ) as Environment[];
    const currentOrganizationPlanData =
      organization.relationships['current-organization-plan']?.data;
    const pendingOrganizationPlanMigrationData =
      organization.relationships['pending-organization-plan-migration']?.data;

    dispatch(setEnvironments(environments));
    dispatch(setOrganization(organization));

    let currentOrganizationPlan = null;
    if (currentOrganizationPlanData?.id) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentOrganizationPlan = included.find(
        (i) => i.id === currentOrganizationPlanData.id,
      ) as OrganizationPlan;
      dispatch(setCurrentOrganizationPlan(currentOrganizationPlan));
    }

    if (pendingOrganizationPlanMigrationData?.id) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const pendingOrganizationPlanMigration = included.find(
        (i) => i.id === pendingOrganizationPlanMigrationData.id,
      ) as OrganizationPlanMigration;
      dispatch(setPendingOrganizationPlanMigration(pendingOrganizationPlanMigration));
    }

    const currentEnvironment =
      environments.find(
        (env) =>
          env.id === StorageClient.getLocalItem(LocalStorageKey.DashboardCurrentEnvironmentId),
      ) || environments[0];

    // Rely on localStorage selected environment; default to first found if none set
    dispatch(setAndPersistCurrentEnvironment(currentEnvironment));

    // TODO: This user code probably belongs in a separate function.
    dispatch(saveCurrentUser(user));
    dispatch(setUserSession(userSession));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    Settings.defaultZone = user.attributes.timezone;

    setIdOnReporters(
      user,
      impersonationSession,
      roles,
      organization,
      currentOrganizationPlan,
      featureFlags,
    );

    const promises = [dispatch(loadThemeList())];

    if (currentEnvironment != null) {
      // Nonblocking fetches for templates. We split this up to allow
      // incremental loading, because the full template response is massive.
      promises.push(dispatch(loadNewAndLegacyInquiryTemplateList()));
      promises.push(dispatch(loadNewAndLegacyInquiryTemplateFilterOptionList()));
      // TODO: Refactor so pages reliant on report templates fetch it themselves if needed.
      // or store report template list in a different redux store than general report templates.
      promises.push(
        dispatch(
          loadReportTemplatesList({
            currentPage: 1,
            pageSize: 100,
            sortParams: {} as any,
            filters: {},
          }),
        ),
      );
    }

    Promise.all(promises).catch((error) => {
      // ignore perm errors
      if (!isPermissionsError(error)) {
        // We explicitly capture the error because an Error Boundary is unable to catch this async error.
        // We also want to make sure these errors get set with the proper tags for Sentry.
        captureException(error);
      }
    });
  };
